
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';

export default function Contact() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });

  const [touched, setTouched] = useState({
    name: false,
    email: false,
    service: false,
    message: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Intersection Observer for fade-in effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleBlur = (field: keyof typeof touched) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const isFieldInvalid = (field: keyof typeof touched) => {
    return touched[field] && !formData[field];
  };

  const getInputClass = (field: keyof typeof touched) => {
    const baseClass = "flex w-full rounded-md px-3 py-2.5 md:py-3 text-sm shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B82F6] bg-white";
    if (isFieldInvalid(field)) {
      return `${baseClass} border-b-2 border-red-500 border-t border-l border-r border-t-gray-300 border-l-gray-300 border-r-gray-300`;
    }
    return `${baseClass} border border-gray-300`;
  };

  const ErrorMessage = ({ field }: { field: keyof typeof touched }) => {
    if (!isFieldInvalid(field)) return null;
    return (
      <p className="text-xs text-red-500 mt-1">{t('contact.fieldRequired')}</p>
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setTouched({
      name: true,
      email: true,
      service: true,
      message: true,
    });

    if (!formData.name || !formData.email || !formData.service || !formData.message) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const { error } = await supabase
        .from('contact_submissions')
        .insert([
          {
            name: formData.name,
            email: formData.email,
            phone: formData.phone || null,
            service: formData.service,
            message: formData.message,
          },
        ]);

      if (error) {
        console.error('Erro ao salvar no Supabase:', error);
        setSubmitStatus('error');
      } else {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
        setTouched({ name: false, email: false, service: false, message: false });

        setTimeout(() => {
          setSubmitStatus('idle');
        }, 5000);
      }
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    let formattedValue = '';
    
    if (value.length > 0) {
      formattedValue = '(' + value.substring(0, 3);
    }
    if (value.length > 3) {
      formattedValue += ') ' + value.substring(3, 6);
    }
    if (value.length > 6) {
      formattedValue += '-' + value.substring(6, 10);
    }
    
    setFormData({
      ...formData,
      phone: formattedValue,
    });
  };

  return (
    <section 
      ref={sectionRef}
      id="contact" 
      className={`py-12 md:py-24 bg-white transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-16">
          {/* Info Section - First on mobile, second on desktop */}
          <div className={`order-1 lg:order-1 transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <span className="inline-block px-3 py-1 md:px-4 md:py-1.5 bg-[#3B82F6]/10 text-[#3B82F6] text-xs md:text-sm font-medium rounded-full mb-3 md:mb-4">
              {t('contact.badge')}
            </span>
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-3 md:mb-6">{t('contact.title')}</h2>
            <p className="text-gray-600 text-sm md:text-lg mb-6 md:mb-10 leading-relaxed">{t('contact.subtitle')}</p>

            {/* Contact Info - Compact grid on mobile */}
            <div className="grid grid-cols-2 md:grid-cols-1 gap-3 md:gap-4 mb-6 md:mb-10">
              <a href="mailto:contact@braxfacility.com" className="flex items-center gap-3 p-3 md:p-5 rounded-xl bg-gray-50 hover:bg-[#0F172A]/5 transition-colors col-span-2 md:col-span-1">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-[#0F172A]/10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-lg md:text-xl text-[#0F172A]"></i>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 mb-0.5">{t('contact.email')}</p>
                  <span className="font-semibold text-[#0F172A] hover:text-[#3B82F6] transition-colors text-xs md:text-base break-all">
                    contact@braxfacility.com
                  </span>
                </div>
              </a>

              <div className="flex items-center gap-3 p-3 md:p-5 rounded-xl bg-gray-50">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-[#0F172A]/10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-lg md:text-xl text-[#0F172A]"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-gray-500 mb-0.5">{t('contact.location')}</p>
                  <p className="font-semibold text-[#0F172A] text-xs md:text-base">{t('contact.locationValue')}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 md:p-5 rounded-xl bg-gray-50">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-[#0F172A]/10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-lg md:text-xl text-[#0F172A]"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-gray-500 mb-0.5">{t('contact.hours')}</p>
                  <p className="font-semibold text-[#0F172A] text-xs md:text-base">{t('contact.hoursValue')}</p>
                </div>
              </div>
            </div>

            {/* Image - Hidden on mobile */}
            <div className="hidden md:block rounded-2xl overflow-hidden h-64 bg-gray-100">
              <img
                src="https://public.readdy.ai/ai/img_res/fae0fa3a7d63911d0e47acfecac3064f.jpg"
                alt="Professional Cleaning Service"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Form Section - Second on mobile, second on desktop */}
          <div className={`order-2 lg:order-2 transition-all duration-700 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <div className="bg-gray-50 rounded-2xl md:rounded-3xl p-5 md:p-10">
              <form
                onSubmit={handleSubmit}
                className="space-y-4 md:space-y-6"
                noValidate
              >
                <div className="grid grid-cols-2 gap-3 md:gap-6">
                  <div className="space-y-1.5 md:space-y-2">
                    <label htmlFor="name" className="text-xs md:text-sm font-medium text-gray-700">
                      {t('contact.formName')} *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onBlur={() => handleBlur('name')}
                      placeholder="John Smith"
                      className={getInputClass('name')}
                    />
                    <ErrorMessage field="name" />
                  </div>

                  <div className="space-y-1.5 md:space-y-2">
                    <label htmlFor="email" className="text-xs md:text-sm font-medium text-gray-700">
                      {t('contact.formEmail')} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={() => handleBlur('email')}
                      placeholder="john@example.com"
                      className={getInputClass('email')}
                    />
                    <ErrorMessage field="email" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 md:gap-6">
                  <div className="space-y-1.5 md:space-y-2">
                    <label htmlFor="phone" className="text-xs md:text-sm font-medium text-gray-700">
                      {t('contact.formPhone')}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      placeholder="(310) 555-0000"
                      maxLength={14}
                      className="flex w-full rounded-md border border-gray-300 px-3 py-2.5 md:py-3 text-sm shadow-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B82F6] bg-white"
                    />
                  </div>

                  <div className="space-y-1.5 md:space-y-2">
                    <label htmlFor="service" className="text-xs md:text-sm font-medium text-gray-700">
                      {t('contact.formService')} *
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      onBlur={() => handleBlur('service')}
                      className={getInputClass('service')}
                    >
                      <option value="">{t('contact.formServicePlaceholder')}</option>
                      <option value="post-construction">{t('contact.formServiceOption1')}</option>
                      <option value="grand-opening">{t('contact.formServiceOption2')}</option>
                      <option value="rough-clean">{t('contact.formServiceOption3')}</option>
                      <option value="final-clean">{t('contact.formServiceOption4')}</option>
                      <option value="other">{t('contact.formServiceOption5')}</option>
                    </select>
                    <ErrorMessage field="service" />
                  </div>
                </div>

                <div className="space-y-1.5 md:space-y-2">
                  <label htmlFor="message" className="text-xs md:text-sm font-medium text-gray-700">
                    {t('contact.formMessage')} *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onBlur={() => handleBlur('message')}
                    placeholder={t('contact.formMessagePlaceholder')}
                    rows={4}
                    maxLength={500}
                    className={`${getInputClass('message')} resize-none`}
                  />
                  <div className="flex justify-between items-center">
                    <ErrorMessage field="message" />
                    <p className="text-xs text-gray-500">{formData.message.length}/500</p>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-12 md:h-14 bg-gradient-to-r from-[#3B82F6] to-[#60A5FA] hover:from-[#2563EB] hover:to-[#3B82F6] text-white text-base md:text-lg font-medium rounded-md shadow transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? t('contact.formSubmitting') : t('contact.formSubmit')}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-3 md:p-4 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-xs md:text-sm text-green-800 text-center">{t('contact.formSuccess')}</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-3 md:p-4 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-xs md:text-sm text-red-800 text-center">
                      Erro ao enviar mensagem. Por favor, tente novamente.
                    </p>
                  </div>
                )}

                <p className="text-center text-xs md:text-sm text-gray-500">{t('contact.formDisclaimer')}</p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
